import { request } from "@/lib/apiClient";
import {
  portfolioStrategyQueryParam,
  type StrategyName,
} from "@/config/strategyBrokers";

export interface FyersUserByUidResponse {
  success: boolean;
  fyers_user?: {
    client_id: string;
    app_id?: string;
    app_secret?: string;
    redirect_url?: string;
    totp_key?: string;
    pin?: string;
    name?: string;
    phone?: number;
    status?: string;
  };
  message?: string;
}

export interface PortfolioDashboardData {
  success: boolean;
  portfolio?: {
    total_value?: number;
    total_investment?: number;
    total_pnl?: number;
    total_pnl_percentage?: number;
    day_pnl?: number;
    day_pnl_percentage?: number;
    available_balance?: number;
    total_trades?: number;
    total_profit?: number;
    monthly_returns?: Array<{
      month: string;
      profit: number;
      return_percentage?: number;
      returnPercentage?: number;
      status: string;
    }>;
    yearly_returns?: Array<{
      year: string;
      profit: number;
      return_percentage: number;
      status: string;
    }>;
  };
  monthly_returns?: unknown;
  yearly_returns?: unknown;
  trading_info?: {
    strategy_status?: string;
    trading_period?: string;
    trading_start_date?: string;
    trading_end_date?: string;
    user_status?: string;
  };
  last_updated?: string;
  strategy?: string;
}

export interface OrdersResponse {
  success: boolean;
  data: any[];
  total?: number;
}

export interface TradesResponse {
  success: boolean;
  data: any[];
  total?: number;
}

function portfolioPath(
  endpoint: "cached" | "dashboard",
  clientId: string,
  strategy?: StrategyName | string | null
) {
  const base = `/portfolio/${endpoint}/${encodeURIComponent(clientId)}`;
  const strategyParam = portfolioStrategyQueryParam(strategy);
  if (!strategyParam) return base;
  const qs = new URLSearchParams({ strategy: strategyParam });
  return `${base}?${qs.toString()}`;
}

export const portfolioService = {
  getFyersUserByFirebaseUid: (firebaseUid: string) =>
    request<FyersUserByUidResponse>(
      `/fyers/user/${firebaseUid}?minimal=1`
    ),

  getCachedPortfolio: (
    clientId: string,
    strategy?: StrategyName | string | null
  ) =>
    request<PortfolioDashboardData>(portfolioPath("cached", clientId, strategy)),

  getPortfolioDashboard: (
    clientId: string,
    strategy?: StrategyName | string | null
  ) =>
    request<PortfolioDashboardData>(
      portfolioPath("dashboard", clientId, strategy)
    ),

  getUserOrders: (firebaseUid: string, page: number = 1, limit: number = 50) =>
    request<OrdersResponse>(`/portfolio/orders/${firebaseUid}?page=${page}&limit=${limit}`),

  getUserTrades: (firebaseUid: string, page: number = 1, limit: number = 50) =>
    request<TradesResponse>(`/portfolio/trades/${firebaseUid}?page=${page}&limit=${limit}`),
};

export default portfolioService;
